import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useUserPermissions } from '@shared/hooks/useUserPermissions';
import { urlMyspreadApi } from '@apis/urls';

// Debug: Importación directa para verificar que funcione
import { getUserPermissions } from '../../../shared/helpers/api/mantenedores/permissions';

const TestApiConnection = () => {
  const currentUser = useSelector(state => state.auth.user);
  
  // Log para debug
  console.log('🔍 TestApiConnection mounted:', { 
    currentUser: currentUser?.email,
    urlMyspreadApi,
    getUserPermissions: typeof getUserPermissions
  });
  
  const { 
    permissions, 
    loading, 
    error, 
    hasModuleAccess,
    loadPermissions 
  } = useUserPermissions();
  
  const [testResults, setTestResults] = useState(null);

  useEffect(() => {
    console.log('🔍 TestApiConnection - Estado actual:', { 
      loading, 
      error, 
      permissions,
      hasModuleAccess: typeof hasModuleAccess
    });
    
    if (!loading && !error && permissions) {
      // Realizar pruebas de acceso a módulos de mantenedores
      const tests = {
        // Acceso general al módulo mantenedores
        'mantenedores (módulo general)': hasModuleAccess('mantenedores'),
        
        // Acceso a páginas específicas de mantenedores
        'directorio (conductores)': hasModuleAccess('directorio'),
        'empresas': hasModuleAccess('empresas'),
        'estados': hasModuleAccess('estados'),
        'extras': hasModuleAccess('extras'),
        'generales': hasModuleAccess('generales'),
        'rutas': hasModuleAccess('rutas'),
        'tarifarios': hasModuleAccess('tarifarios'),
        'usuarios': hasModuleAccess('usuarios')
      };
      
      console.log('🧪 Resultados de tests:', tests);
      setTestResults(tests);
    }
  }, [permissions, loading, error, hasModuleAccess]);

  const handleRefreshPermissions = () => {
    console.log('🔄 Recargando permisos...');
    if (loadPermissions && typeof loadPermissions === 'function') {
      loadPermissions();
    } else {
      console.error('❌ loadPermissions no está disponible o no es una función');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'loading': return '#e3f2fd';
      case 'error': return '#ffebee';
      case 'success': return '#e8f5e8';
      default: return '#f9f9f9';
    }
  };

  const getStatusTextColor = (status) => {
    switch (status) {
      case 'error': return '#c62828';
      case 'success': return '#2e7d32';
      default: return '#333';
    }
  };

  return (
    <div style={{ padding: '20px', fontFamily: 'Arial, sans-serif', maxWidth: '1200px', margin: '0 auto' }}>
      <h1>🧪 Test de Conexión API - Sistema de Permisos</h1>
      
      {/* Información del usuario */}
      {currentUser && (
        <div style={{ 
          padding: '15px', 
          backgroundColor: '#f0f8ff', 
          borderRadius: '8px',
          marginBottom: '20px',
          border: '1px solid #b3d9ff'
        }}>
          <h3>👤 Usuario Conectado:</h3>
          <p><strong>Email:</strong> {currentUser.email}</p>
          <p><strong>Nombre:</strong> {currentUser.displayName || 'No disponible'}</p>
          <p><strong>UID:</strong> {currentUser.uid}</p>
        </div>
      )}

      {/* Configuración de la API */}
      <div style={{ 
        padding: '15px', 
        backgroundColor: '#f9f9f9', 
        borderRadius: '8px',
        marginBottom: '20px',
        border: '1px solid #ddd'
      }}>
        <h3>⚙️ Configuración de la API:</h3>
        <p><strong>Endpoint:</strong> <code>{urlMyspreadApi}/mantenedores/usuarios/permisos/me</code></p>
        <p><strong>Método:</strong> GET</p>
        <p><strong>Autenticación:</strong> Basic Auth + Headers de usuario</p>
      </div>
      
      <div style={{ marginBottom: '20px' }}>
        <button 
          onClick={handleRefreshPermissions}
          disabled={loading}
          style={{
            padding: '12px 24px',
            backgroundColor: loading ? '#ccc' : '#4CAF50',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: loading ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            fontWeight: 'bold'
          }}
        >
          {loading ? '🔄 Cargando...' : '🔄 Recargar Permisos'}
        </button>
      </div>

      {/* Estados de carga, error, éxito */}
      {loading && (
        <div style={{ 
          padding: '20px', 
          backgroundColor: getStatusColor('loading'), 
          borderRadius: '8px',
          marginBottom: '20px',
          border: '1px solid #90caf9'
        }}>
          <h3 style={{ margin: '0 0 10px 0' }}>🔄 Cargando permisos...</h3>
          <p style={{ margin: 0 }}>Conectando con la API y obteniendo permisos del usuario</p>
        </div>
      )}

      {error && (
        <div style={{ 
          padding: '20px', 
          backgroundColor: getStatusColor('error'), 
          borderRadius: '8px',
          marginBottom: '20px',
          border: '1px solid #f48fb1',
          color: getStatusTextColor('error')
        }}>
          <h3 style={{ margin: '0 0 10px 0' }}>❌ Error al cargar permisos</h3>
          <p style={{ margin: '0 0 15px 0' }}><strong>Detalles:</strong> {error}</p>
          <div style={{ fontSize: '14px', color: '#666' }}>
            <p><strong>Posibles causas:</strong></p>
            <ul>
              <li>El servidor no está disponible</li>
              <li>Problemas de autenticación</li>
              <li>El usuario no tiene permisos configurados</li>
              <li>Error en la configuración de CORS</li>
            </ul>
          </div>
        </div>
      )}

      {permissions && (
        <div style={{ 
          padding: '20px', 
          backgroundColor: getStatusColor('success'), 
          borderRadius: '8px',
          marginBottom: '20px',
          border: '1px solid #a5d6a7'
        }}>
          <h3 style={{ margin: '0 0 10px 0', color: getStatusTextColor('success') }}>
            ✅ Permisos cargados exitosamente
          </h3>
          <p style={{ margin: 0 }}>La API respondió correctamente y los permisos han sido procesados</p>
        </div>
      )}

      {/* Datos de permisos recibidos */}
      {permissions && (
        <div style={{ marginBottom: '30px' }}>
          <h3>📋 Datos de Permisos Procesados:</h3>
          <div style={{ 
            backgroundColor: '#f8f9fa', 
            padding: '20px', 
            borderRadius: '8px',
            border: '1px solid #dee2e6'
          }}>
            <h4>Información General:</h4>
            <ul>
              <li><strong>Email:</strong> {permissions.email}</li>
              <li><strong>User ID:</strong> {permissions.userId}</li>
              <li><strong>Tiene permisos:</strong> {permissions.hasAnyPermissions ? 'Sí' : 'No'}</li>
              <li><strong>Total de módulos:</strong> {Object.keys(permissions.modules || {}).length}</li>
            </ul>
          </div>
          
          <details style={{ marginTop: '15px' }}>
            <summary style={{ cursor: 'pointer', padding: '10px', backgroundColor: '#e9ecef', borderRadius: '4px' }}>
              🔍 Ver datos JSON completos
            </summary>
            <pre style={{ 
              backgroundColor: '#f8f9fa', 
              padding: '20px', 
              borderRadius: '8px',
              overflow: 'auto',
              fontSize: '12px',
              border: '1px solid #dee2e6',
              marginTop: '10px'
            }}>
              {JSON.stringify(permissions, null, 2)}
            </pre>
          </details>
        </div>
      )}

      {/* Resultados de pruebas de acceso */}
      {testResults && (
        <div style={{ marginBottom: '30px' }}>
          <h3>🧪 Resultados de Pruebas de Acceso por Módulo:</h3>
          <div style={{ 
            backgroundColor: 'white', 
            borderRadius: '8px', 
            overflow: 'hidden',
            border: '1px solid #dee2e6'
          }}>
            <table style={{ 
              width: '100%', 
              borderCollapse: 'collapse'
            }}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa' }}>
                  <th style={{ 
                    padding: '15px', 
                    textAlign: 'left', 
                    borderBottom: '2px solid #dee2e6',
                    fontWeight: 'bold'
                  }}>
                    Módulo
                  </th>
                  <th style={{ 
                    padding: '15px', 
                    textAlign: 'center', 
                    borderBottom: '2px solid #dee2e6',
                    fontWeight: 'bold'
                  }}>
                    Estado de Acceso
                  </th>
                  <th style={{ 
                    padding: '15px', 
                    textAlign: 'center', 
                    borderBottom: '2px solid #dee2e6',
                    fontWeight: 'bold'
                  }}>
                    Descripción
                  </th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(testResults).map(([module, hasAccess]) => (
                  <tr key={module} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ 
                      padding: '15px',
                      fontWeight: '500'
                    }}>
                      {module}
                    </td>
                    <td style={{ 
                      padding: '15px', 
                      textAlign: 'center',
                      backgroundColor: hasAccess ? '#d4edda' : '#f8d7da',
                      color: hasAccess ? '#155724' : '#721c24',
                      fontWeight: 'bold'
                    }}>
                      {hasAccess ? '✅ Permitido' : '❌ Denegado'}
                    </td>
                    <td style={{ 
                      padding: '15px', 
                      textAlign: 'center',
                      color: '#666',
                      fontSize: '14px'
                    }}>
                      {hasAccess ? 'Usuario tiene acceso a este módulo' : 'Sin permisos para este módulo'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Instrucciones */}
      <div style={{ 
        marginTop: '40px', 
        padding: '20px', 
        backgroundColor: '#f8f9fa', 
        borderRadius: '8px',
        border: '1px solid #dee2e6'
      }}>
        <h3>📖 Instrucciones de Uso:</h3>
        <ol style={{ paddingLeft: '20px' }}>
          <li>Este componente se conecta automáticamente a la API al cargar</li>
          <li>Verifica permisos usando el usuario actualmente conectado</li>
          <li>Puedes recargar los permisos manualmente con el botón superior</li>
          <li>Los resultados muestran qué módulos están disponibles para el usuario</li>
          <li>Revisa la consola del navegador para logs detallados</li>
        </ol>
        
        <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#fff3cd', borderRadius: '4px' }}>
          <strong>⚠️ Nota importante:</strong> Este es un componente de prueba. En producción, los permisos se verifican automáticamente en cada componente que los requiera.
        </div>
      </div>
    </div>
  );
};

export default TestApiConnection;
